import {
  useCreateCorrespondenceThreadMutation,
  getFilteredCorrespondence,
  CorrespondenceEntityName,
  getFullName,
  useMarkCorrespondencesAsReadMutation,
} from "toptaal-shared"
import { correspondenceQueryKeys } from "toptaal-shared"
import {
  createFileRoute,
  useNavigate,
  useParams,
  useRouter,
  useSearch,
} from "@tanstack/react-router"
import {
  CorrespondenceEntityType,
  CorrespondenceOverview,
  CorrespondenceThread,
  useCorrespondenceRouteWithUrlState,
  createTableSearchSchema,
} from "toptaal-shared"
import { checkOrganizationPermissions } from "@/utils/checkOrganizationPermissions"
import { organizationUserCan } from "@/utils/organizationUserCan"
import { useOrganizationUserQuery } from "@/api/queries/organizationUser/useOrganizationUserQuery"
import { useStudentSuspenseQuery } from "@/api/queries/students/useStudentSuspenseQuery"

export const Route = createFileRoute(
  "/beheer/cursisten/$studentId/correspondentie/",
)({
  component: RouteComponent,
  validateSearch: createTableSearchSchema(),
  beforeLoad: async () => {
    return checkOrganizationPermissions("read", "studentCorrespondence")
  },
})

function RouteComponent() {
  const { studentId } = useParams({
    from: "/beheer/cursisten/$studentId/correspondentie/",
  })
  const searchParams = useSearch({
    from: "/beheer/cursisten/$studentId/correspondentie/",
  })
  const navigate = useNavigate({
    from: "/beheer/cursisten/$studentId/correspondentie/",
  })

  const router = useRouter()
  const { data: user } = useOrganizationUserQuery()
  const {
    data: { data: student },
  } = useStudentSuspenseQuery(Number(studentId))

  const fullName = getFullName({
    firstName: student.firstName,
    lastName: student.lastName,
  })

  const entityType: CorrespondenceEntityType = "Student"
  const entityId = Number(studentId)
  const entityName: CorrespondenceEntityName = fullName

  const correspondenceProps = useCorrespondenceRouteWithUrlState({
    entityType,
    entityId,
    queryKey: correspondenceQueryKeys.student(entityId, entityType),
    queryFn: (filters) =>
      getFilteredCorrespondence({ entityId, entityType, filters }),
    useCreateMutation: useCreateCorrespondenceThreadMutation,
    searchParams,
    navigate,
    storageKey: `/beheer/cursisten/${studentId}/correspondentie`,
  })

  const markAsReadMutation = useMarkCorrespondencesAsReadMutation({
    queryKey: correspondenceQueryKeys.student(entityId, entityType),
  })

  const handleNavigateToThread = (thread: CorrespondenceThread) => {
    if (thread.unreadCount > 0) {
      markAsReadMutation.mutate([thread.id])
    }

    router.navigate({
      to: "/beheer/cursisten/$studentId/correspondentie/$threadId",
      replace: true,
      params: {
        threadId: String(thread.id),
        studentId,
      },
    })
  }

  return (
    <CorrespondenceOverview
      {...correspondenceProps}
      handleNavigateToThread={handleNavigateToThread}
      contextType={entityType}
      entityName={entityName}
      canCreateCorrespondence={organizationUserCan(
        "create",
        "studentCorrespondence",
      )}
      correspondencePermission={{
        root: "org",
        roles: user.roles,
        baseUserId: user.baseUserId,
      }}
    />
  )
}
